/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * UserDataCache.ts
 *
 * Serviço para cache de dados do usuário em memória
 * Implementa padrão Singleton para garantir uma única instância de cache
 * Inclui TTL (Time To Live) e deduplicação de requisições concorrentes
 */

import { getApi } from "@/shared/utils/fetchApi";
import { API_CONFIG } from "@/infrastructure/config/api";

// Tipo para dados do usuário
export interface UserData {
  id: string;
  email?: string;
  [key: string]: any;
}

// Tipo para resposta da sessão
export interface SessionResponse {
  data: {
    session: {
      user: UserData;
    } | null;
  };
  error: any;
}

export class UserDataCache {
  private static instance: UserDataCache;
  private userData: UserData | null = null;
  private lastFetchTime: number = 0;
  private ttlMs: number = 5 * 60 * 1000; // 5 minutos por padrão
  private fetchPromise: Promise<SessionResponse> | null = null;

  /**
   * Construtor privado para implementar Singleton
   */
  private constructor() {}

  /**
   * Obtém a instância única do cache
   */
  public static getInstance(): UserDataCache {
    if (!UserDataCache.instance) {
      UserDataCache.instance = new UserDataCache();
    }
    return UserDataCache.instance;
  }

  /**
   * Configura o TTL (Time To Live) do cache
   * @param minutes Tempo em minutos
   */
  public setTTL(minutes: number): void {
    this.ttlMs = minutes * 60 * 1000;
  }

  /**
   * Obtém os dados do usuário, usando cache quando disponível
   * @returns Promise com os dados da sessão
   */
  public async getSession(): Promise<SessionResponse> {
    // Retornar dados em cache se ainda válidos
    if (this.isCacheValid()) {
      return {
        data: {
          session: this.userData ? { user: this.userData } : null,
        },
        error: null,
      };
    }

    // Deduplicar requisições concorrentes
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Buscar dados atualizados
    this.fetchPromise = this.fetchFreshUserData();

    try {
      const response = await this.fetchPromise;
      return response;
    } finally {
      this.fetchPromise = null;
    }
  }

  /**
   * Obtém apenas o ID do usuário do cache
   * @returns Promise com o ID do usuário ou null
   */
  public async getUserId(): Promise<string | null> {
    const { data } = await this.getSession();
    return data.session?.user?.id || null;
  }

  /**
   * Invalida o cache forçando uma nova busca na próxima requisição
   */
  public invalidateCache(): void {
    this.userData = null;
    this.lastFetchTime = 0;
  }

  /**
   * Força uma atualização dos dados do cache
   * @returns Promise com os dados atualizados
   */
  public async refreshCache(): Promise<SessionResponse> {
    this.invalidateCache();
    return this.getSession();
  }

  /**
   * Verifica se o cache ainda é válido
   */
  private isCacheValid(): boolean {
    return (
      this.userData !== null && Date.now() - this.lastFetchTime < this.ttlMs
    );
  }

  /**
   * Busca dados atualizados do usuário na API
   */
  private async fetchFreshUserData(): Promise<SessionResponse> {
    try {
      const baseUrl = API_CONFIG.API_ROOT_URL;
      const raw = await getApi<any>(`${baseUrl}/auth/me`);

      // Normalizar envelope: legacy = { success, data: { user } }, v2 = User DTO flat
      // (o /auth/me do api retorna o DTO na raiz, NÃO { user }).
      const user: UserData | null = raw ?? null;

      if (user) {
        this.userData = user;
        this.lastFetchTime = Date.now();
        return { data: { session: { user: this.userData } }, error: null };
      }

      this.userData = null;
      return { data: { session: null }, error: null };
    } catch (error) {
      console.error("[UserDataCache] Erro ao buscar dados do usuário:", error);
      return { data: { session: null }, error };
    }
  }
}

// Exportar instância singleton para uso fácil
export const userDataCache = UserDataCache.getInstance();
